/**
 * @file App.tsx
 * @description Point d'entrée principal de l'application avec gestion de l'authentification et des routes.
 */

import { BrowserRouter, useLocation } from 'react-router-dom';
import './App.css';
import RouteConfig from './routes/RouteConfig';
import AppHeader from './components/AppHeader/AppHeader';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { useEffect } from 'react';
import { useTransactionsStore } from './store/transactions/useTransactionsStore';
import { createIDBPersister } from './utils/createIDBPersister';
import { SyncSelectedCompany } from './utils/query/SyncSelectedCompany';
import { message, App as AntdApp } from 'antd';
import { isHttpError } from './errors/httpErrors';

/**
 * Callback global d'erreur pour React Query.
 * Affiche un message utilisateur si l'erreur n'a pas été gérée localement.
 * Les mutations/queries avec meta.skipGlobalError = true sont ignorées.
 */
const handleGlobalQueryError = (error: unknown, query: { meta?: Record<string, unknown> }) => {
  if (query.meta?.skipGlobalError) return;
  const errorMessage = isHttpError(error) ? error.message : 'Une erreur inattendue est survenue.';
  message.error(errorMessage);
};

const handleGlobalMutationError = (
  error: unknown,
  _variables: unknown,
  _context: unknown,
  mutation: { meta?: Record<string, unknown> },
) => {
  if (mutation.meta?.skipGlobalError) return;
  const errorMessage = isHttpError(error) ? error.message : 'Une erreur inattendue est survenue.';
  message.error(errorMessage);
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    onError: handleGlobalQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleGlobalMutationError,
  }),
});

persistQueryClient({
  queryClient,
  persister: createIDBPersister(),
});

/**
 * @component AppContent
 * @description Contenu principal (séparé pour pouvoir utiliser useLocation).
 */
const AppContent = () => {
  const location = useLocation();
  const setSelectedTransactions = useTransactionsStore((state) => state.setSelectedTransactions);

  useEffect(() => {
    if (
      location.pathname === '/cabinet/production-comptable/journal' ||
      location.pathname === '/cabinet/production-comptable/grand-livre'
    ) {
      setSelectedTransactions([]);
    }
  }, [location.pathname, setSelectedTransactions]);

  const showHeader = !location?.pathname.startsWith('/conclusions');

  return (
    <div className="screen">
      {/* ✅ Header affiché seulement si showHeader = true */}
      {showHeader && (
        <div className="screen-header">
          <AppHeader />
        </div>
      )}

      <div className="middle">
        <RouteConfig />
      </div>
      <footer></footer>
    </div>
  );
};

/**
 * @component App
 * @description Composant principal englobant QueryClient, BrowserRouter, etc.
 */
function App() {
  return (
    <div className="App">
      <AntdApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <SyncSelectedCompany />
            <AppContent />
          </BrowserRouter>
        </QueryClientProvider>
      </AntdApp>
    </div>
  );
}

export default App;
